<script setup>
import { watch, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import Editor from 'dashboard/components-next/Editor/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ContactNoteItem from 'next/Contacts/ContactsSidebar/components/ContactNoteItem.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const store = useStore();
const currentUser = useMapGetter('getCurrentUser');
const uiFlags = useMapGetter('contactNotes/getUIFlags');
const notesByContact = useMapGetter('contactNotes/getAllNotesByContactId');
const isFetchingNotes = computed(() => uiFlags.value.isFetching);
const isCreatingNote = computed(() => uiFlags.value.isCreating);
const isSavingNote = computed(
  () => uiFlags.value.isUpdating || uiFlags.value.isDeleting
);
const contactId = computed(() => props.contactId);
const noteContent = ref('');
const dangSoanGhiChu = ref(false);
const notes = computed(() => {
  if (!contactId.value) {
    return [];
  }
  return notesByContact.value(contactId.value) || [];
});

const getWrittenBy = ({ user } = {}) => {
  const currentUserId = currentUser.value?.id;
  return user?.id === currentUserId
    ? t('CONTACTS_LAYOUT.SIDEBAR.NOTES.YOU')
    : user?.name || t('CONVERSATION.BOT');
};

const moSoanGhiChu = () => {
  if (!contactId.value) {
    return;
  }

  noteContent.value = '';
  dangSoanGhiChu.value = true;
};

const dongSoanGhiChu = () => {
  dangSoanGhiChu.value = false;
  noteContent.value = '';
};

const onAdd = async () => {
  if (!contactId.value || !noteContent.value || isCreatingNote.value) {
    return;
  }

  await store.dispatch('contactNotes/create', {
    content: noteContent.value,
    contactId: contactId.value,
  });
  noteContent.value = '';
  dongSoanGhiChu();
  useAlert(t('BONBOZ.SIDEBAR.GHI_CHU_DA_LUU'));
};

// Enter lưu, Shift+Enter xuống dòng. Bắt ở pha capture để chặn trước khi trình
// soạn thảo tự chèn dòng mới. `isComposing` là bắt buộc: bộ gõ tiếng Việt dựng
// chữ bằng phím, không có nó thì gõ "chuẩn" bị lưu ngay ở "chuân".
const onEditorKeydown = event => {
  if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
  event.preventDefault();
  event.stopPropagation();
  onAdd();
};

const onDelete = async noteId => {
  if (!contactId.value || !noteId) {
    return;
  }

  await store.dispatch('contactNotes/delete', {
    noteId,
    contactId: contactId.value,
  });
  useAlert(t('BONBOZ.SIDEBAR.GHI_CHU_DA_XOA'));
};

const onUpdate = async ({ id, content }) => {
  await store.dispatch('contactNotes/update', {
    contactId: contactId.value,
    noteId: id,
    content,
  });
  useAlert(t('BONBOZ.SIDEBAR.GHI_CHU_DA_LUU'));
};

const onTogglePin = async ({ id, pinned }) => {
  await store.dispatch('contactNotes/update', {
    contactId: contactId.value,
    noteId: id,
    pinned,
  });
  useAlert(
    t(
      pinned
        ? 'BONBOZ.SIDEBAR.GHI_CHU_DA_GHIM'
        : 'BONBOZ.SIDEBAR.GHI_CHU_DA_BO_GHIM'
    )
  );
};

watch(
  contactId,
  newContactId => {
    dongSoanGhiChu();
    if (newContactId) {
      store.dispatch('contactNotes/get', { contactId: newContactId });
    }
  },
  { immediate: true }
);
</script>

<template>
  <div>
    <!-- BonBoZ: tiêu đề thẻ nằm cùng hàng với nút thêm. Thẻ ghi chú không phải
         mục gập nên không có phần đầu sẵn như các mục khác, mà thiếu tiêu đề
         thì nhìn xuống chỉ thấy một đống chữ không biết là gì. -->
    <div class="flex items-center justify-between gap-2 px-3 pt-2 pb-1">
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES') }}
      </span>
      <NextButton
        ghost
        xs
        icon="i-lucide-plus"
        :label="$t('CONTACTS_LAYOUT.SIDEBAR.NOTES.ADD_NOTE')"
        :disabled="!contactId || isFetchingNotes || dangSoanGhiChu"
        @click="moSoanGhiChu"
      />
    </div>

    <!-- BonBoZ: soạn ghi chú NGAY TRONG sidebar. Trước đây mở lớp phủ giữa màn
         hình, che mất khung chat — đúng lúc cần nhìn lại tin khách để ghi. -->
    <div v-if="dangSoanGhiChu" class="flex flex-col gap-2 px-4 pb-3">
      <div @keydown.capture="onEditorKeydown">
        <!-- `Editor` mặc định CẮT CỤT ở 200 ký tự, không cảnh báo gì. Ghi chú
             khách thường dài hơn thế, nên phải khai rõ. Cột `content` là text
             nên phía DB không có giới hạn nào. -->
        <Editor
          v-model="noteContent"
          focus-on-mount
          :max-length="2000"
          :placeholder="t('BONBOZ.SIDEBAR.GHI_CHU_PLACEHOLDER')"
          class="[&>div]:!border-n-weak [&>div]:px-2 [&>div]:py-2"
        />
      </div>
      <div class="flex items-center justify-end gap-2">
        <NextButton
          ghost
          xs
          slate
          :label="t('BONBOZ.SIDEBAR.GHI_CHU_HUY')"
          @click="dongSoanGhiChu"
        />
        <NextButton
          solid
          blue
          xs
          :label="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.SAVE')"
          :is-loading="isCreatingNote"
          :disabled="!noteContent || isCreatingNote"
          @click="onAdd"
        />
      </div>
    </div>

    <div
      v-if="isFetchingNotes"
      class="flex items-center justify-center py-8 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div
      v-else-if="notes.length"
      class="flex flex-col max-h-[300px] overflow-y-auto"
    >
      <ContactNoteItem
        v-for="note in notes"
        :key="note.id"
        class="py-4 last-of-type:border-b-0 px-4"
        :note="note"
        :written-by="getWrittenBy(note)"
        :is-saving="isSavingNote"
        allow-delete
        allow-edit
        allow-pin
        collapsible
        @delete="onDelete"
        @update="onUpdate"
        @toggle-pin="onTogglePin"
      />
    </div>
    <p v-else class="px-6 py-6 text-sm leading-6 text-center text-n-slate-11">
      {{ t('CONTACTS_LAYOUT.SIDEBAR.NOTES.CONVERSATION_EMPTY_STATE') }}
    </p>
  </div>
</template>
